import { createAsyncThunk } from '@reduxjs/toolkit'
import axios, { AxiosError } from 'axios'

import {
  AffectToUsage,
  AssetIdentifier,
  CreateDefectFormData,
  CreateDefectPayload,
  CreateDefectResponse,
  DefectGroup,
  DefectTree,
  LimitationOfUsage,
  OptionTypes,
  PositionInVehicle,
} from '../../components/create-defect/types'
import { DEFECT_ACCEPTED_FILE_TYPES } from '../../constants'
import { apiGET, apiPOST } from '../../lib/api'
import { createFetchAction } from '../defect-common'

interface CreateDefectError {
  message: string
}

const formatPayload = (payload: CreateDefectFormData): CreateDefectPayload => ({
  affectToUsage: payload.affectToUsage,
  comment: payload.comment,
  criticality: +payload.criticality,
  defectGroup: payload.defectGroup,
  installationGroup: payload.installationGroup,
  installationItem: `         ${payload.vehicleType}`, // spaces are intentional
  installationSerialNumber: payload.vehicleSerialNumber,
  issuedTime: payload.issuedTime.toISOString().split('.')[0] + 'Z',
  reportedDefectCode: payload.defectCode,
  restrictionCode: payload.restrictionCode,
  vehiclePosition: payload.positionInVehicle,
})

const createDefect = (payload: CreateDefectPayload): Promise<CreateDefectResponse> =>
  apiPOST('defect', payload)

const selectedDigitalAssets = new Map<string, File>()

export const removeSelectedDigitalAsset = (id: string): void => {
  selectedDigitalAssets.delete(id)
}

export const clearSelectedDigitalAssets = (): void => {
  selectedDigitalAssets.clear()
}

type DigitalAssetUploadUrl = {
  url: string
}

// FC API authorizes attachment uploads against its own replica of LN data, so a
// freshly created defect is rejected with 409 until replication catches up.
const UPLOAD_URL_RETRY_INTERVAL_MS = 5000
const UPLOAD_URL_RETRY_TIMEOUT_MS = 30000

const delay = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms))

const requestUploadUrl = async (
  defectId: string,
  filename: string,
  contentType: string
): Promise<DigitalAssetUploadUrl> => {
  const deadline = Date.now() + UPLOAD_URL_RETRY_TIMEOUT_MS
  let lastError: unknown = new Error('Requesting digital asset upload URL timed out')

  while (Date.now() < deadline) {
    try {
      return await apiPOST<DigitalAssetUploadUrl>('digitalAssets/uploadUrl', {
        defectId,
        filename,
        contentType,
      })
    } catch (err) {
      const defectNotReady = err instanceof AxiosError && err.response?.status === 409
      if (!defectNotReady) throw err
      lastError = err
      await delay(UPLOAD_URL_RETRY_INTERVAL_MS)
    }
  }

  throw lastError
}

// The browser may report an empty MIME type for accepted files whose extension the
// OS does not recognize (e.g. .heic outside Apple devices), so fall back to the
// extension mapping the dropzone accepts files by.
export const mimeFromExtension = (filename: string): string | undefined => {
  const extension = filename.slice(filename.lastIndexOf('.')).toLowerCase()
  return Object.entries(DEFECT_ACCEPTED_FILE_TYPES).find(([, extensions]) =>
    extensions.includes(extension)
  )?.[0]
}

const uploadDigitalAsset = async (defectId: string, file: File): Promise<void> => {
  const contentType = file.type || mimeFromExtension(file.name) || 'application/octet-stream'
  const { url } = await requestUploadUrl(defectId, file.name, contentType)
  // Plain axios instead of the shared api instance: the pre-signed S3 URL must not
  // get the app baseURL, auth headers or request timeout applied to it.
  await axios.put(url, file, { headers: { 'Content-Type': contentType } })
}

export const createDefectAction = createAsyncThunk<
  {
    response: CreateDefectResponse
    request: CreateDefectPayload
    attachmentUploadFailures: string[]
  },
  CreateDefectFormData | undefined,
  { rejectValue: CreateDefectError }
>('defect/createDefect', async (payload, thunkApi) => {
  try {
    if (!payload) {
      return thunkApi.rejectWithValue({
        message: 'Failed to create defect',
      })
    }
    const formattedPayload = formatPayload(payload)
    const response = await createDefect(formattedPayload)

    if (!response.id) {
      return thunkApi.rejectWithValue({
        message: 'Failed to create defect',
      })
    }

    const uploadResults = await Promise.allSettled(
      payload.digitalAssetUuids.map(async (id) => {
        const file = selectedDigitalAssets.get(id)
        if (!file) throw new Error(`Digital asset ${id} is not available`)
        await uploadDigitalAsset(response.id, file)
        return file.name
      })
    )
    const attachmentUploadFailures = uploadResults.flatMap((result, index) =>
      result.status === 'rejected'
        ? [selectedDigitalAssets.get(payload.digitalAssetUuids[index])?.name ?? 'unknown']
        : []
    )

    clearSelectedDigitalAssets()
    return { response, request: formattedPayload, attachmentUploadFailures }
  } catch (err) {
    return thunkApi.rejectWithValue({
      message: 'Failed to create defect',
    })
  }
})

const detectImageMimeFromBase64 = (base64: string): string | null => {
  try {
    const head = atob(base64.slice(0, 32))
    const code = (i: number) => head.charCodeAt(i)
    if (code(0) === 0xff && code(1) === 0xd8 && code(2) === 0xff) return 'image/jpeg'
    if (code(0) === 0x89 && code(1) === 0x50 && code(2) === 0x4e && code(3) === 0x47)
      return 'image/png'
    if (code(0) === 0x47 && code(1) === 0x49 && code(2) === 0x46 && code(3) === 0x38)
      return 'image/gif'
    if (head.slice(0, 4) === 'RIFF' && head.slice(8, 12) === 'WEBP') return 'image/webp'
    return null
  } catch {
    return null
  }
}

const file2Base64 = (file: File): Promise<string> => {
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader()
    reader.readAsDataURL(file)
    reader.onload = () => {
      const raw = reader.result?.toString()
      if (raw) {
        const withoutMetaDataType = raw.split(',')[1]
        resolve(withoutMetaDataType)
      } else {
        reject(new Error('Failed to convert file to base64'))
      }
    }
    reader.onerror = (error) => reject(error)
  })
}

export type PostDigitalAssetResult = AssetIdentifier & {
  base64?: string
  mimeType: string
}

export const postDigitalAssetAction = createAsyncThunk<
  PostDigitalAssetResult,
  File,
  { rejectValue: CreateDefectError }
>('defect/postDigitalAsset', async (payload: File, thunkApi) => {
  try {
    const id = crypto.randomUUID()
    selectedDigitalAssets.set(id, payload)
    const base64 = payload.type.startsWith('image/') ? await file2Base64(payload) : undefined
    if (thunkApi.signal.aborted) {
      selectedDigitalAssets.delete(id)
      return thunkApi.rejectWithValue({
        message: 'Digital asset upload aborted',
      })
    }
    const detectedMime = base64 ? detectImageMimeFromBase64(base64) : null
    return {
      id,
      filename: payload.name,
      base64,
      mimeType: detectedMime ?? payload.type ?? 'application/octet-stream',
    }
  } catch (err) {
    return thunkApi.rejectWithValue({
      message: 'Failed to send digital asset',
    })
  }
})

interface OptionFetch<T extends keyof OptionTypes> {
  id: T
  data: OptionTypes[T]
}

export type OptionsWithID<T extends keyof OptionTypes> = Promise<OptionFetch<T>>

const getLimitationsOfUsage = async ({
  serializedItemIds,
}: {
  serializedItemIds: string[]
}): OptionsWithID<'limitationsOfUsage'> => {
  const response = await apiGET<{ limitationsOfUsage: LimitationOfUsage[] }>(
    `limitationsOfUsage?serializedItemIds=${serializedItemIds.join(',')}`
  )
  const data = response.limitationsOfUsage.map(({ id, description }) => ({
    id,
    description,
  }))
  return { data, id: 'limitationsOfUsage' }
}

const getPositions = async ({
  serializedItemId,
  defectCode,
}: {
  serializedItemId: string
  defectCode: string
}): OptionsWithID<'positions'> => {
  if (!serializedItemId?.length || !defectCode?.length) return { data: [], id: 'positions' }

  const response = await apiGET<{ positions: PositionInVehicle[] }>(
    `positions?serializedItemId=${serializedItemId}&defectCode=${defectCode}`
  )
  const data = response.positions.map(({ position, description }) => ({
    position,
    description,
  }))
  return { data, id: 'positions' }
}

const getDefects = async (itemId: string): OptionsWithID<'defectTrees'> => {
  const response = await apiGET<{ defectTrees: DefectTree[] }>(`defectTrees?itemId=${itemId}`)
  const data = response.defectTrees.map(
    ({
      productGroups,
      defectType,
      criticality,
      defectCode,
      mpgDescription,
      spgDescription,
      vrpg1Description,
      vrpg2Description,
    }) => ({
      productGroups,
      defectType,
      criticality,
      defectCode,
      mpgDescription,
      spgDescription,
      vrpg1Description,
      vrpg2Description,
    })
  )
  return { data, id: 'defectTrees' }
}

const getAffectToUsage = async (): OptionsWithID<'affectToUsage'> => {
  const response = await apiGET<{ codes: AffectToUsage[] }>('codes?group=affecttousage')
  const data = response.codes.map(({ id, description }) => ({
    id,
    description,
  }))
  return { data, id: 'affectToUsage' }
}

const getDefectGroups = async (): OptionsWithID<'defectGroups'> => {
  const response = await apiGET<{ defectGroups: DefectGroup[] }>('defectGroups')
  const groupsOfInterest = response.defectGroups
    .filter((group) => group.useForCreateDefect)
    .sort((a, b) => a.sortIndex - b.sortIndex)
  const data = groupsOfInterest.map(
    ({ id, description, useForCreateDefect, createDefectRequirePicture, type, sortIndex }) => ({
      id,
      description,
      useForCreateDefect,
      createDefectRequirePicture,
      type,
      sortIndex,
    })
  )
  return { data, id: 'defectGroups' }
}

const fetchDefectGroupsAction = createFetchAction('defectGroups', getDefectGroups)
const fetchDefectTreesAction = createFetchAction('defects', getDefects)
const fetchLimitationsOfUsageAction = createFetchAction('limitationsOfUsage', getLimitationsOfUsage)
const fetchPositionsActions = createFetchAction('positions', getPositions)
const fetchAffectToUsageAction = createFetchAction('affectToUsage', getAffectToUsage)

export const options = {
  fetchDefectTreesAction,
  fetchLimitationsOfUsageAction,
  fetchPositionsActions,
  fetchAffectToUsageAction,
  fetchDefectGroupsAction,
}
